<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex inline-flex items-center text-sm text-gray-500"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Tawk.to Integration" />
            </div>

            <BaseSpinner :isLoading="isLoading" />
        </div>
        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <div class="mb-4">
                            <img
                                src="https://img.storekit.com/e7b441d2-91ac-475b-9508-a125ef563352/-/format/auto/-/resize/200x/"
                                alt="Tawk.to"
                                class="h-12 w-auto"
                            />
                        </div>
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                        >
                            Details
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            Configure your Tawk.to live chat widget by providing your Property ID and Widget ID.
                        </p>
                        <p class="mt-2 text-sm text-gray-500">
                            You'll need a Tawk.to account,
                            <a
                                class="text-indigo-600"
                                href="https://www.tawk.to/"
                                rel="noopener"
                                target="_blank"
                                >create one here</a
                            >.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0" v-if="!isLoading">
                    <div class="shadow sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="sm:col-span-6">
                                    <base-input-field
                                        v-model="tawkto.propertyId"
                                        type="text"
                                        label="Property ID"
                                        id="property-id"
                                        placeholder="Enter your Tawk.to Property ID"
                                        :errors="$v.tawkto.propertyId.$error"
                                        errorMessage="This field is required"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <base-input-field
                                        v-model="tawkto.widgetId"
                                        type="text"
                                        label="Widget ID"
                                        id="widget-id"
                                        placeholder="Enter your Tawk.to Widget ID"
                                        :errors="$v.tawkto.widgetId.$error"
                                        errorMessage="This field is required"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <label
                                        for="enabled"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                    >
                                        Integration Status
                                    </label>

                                    <div class="mt-2 flex items-start">
                                        <div class="w-full" v-auto-animate>
                                            <div
                                                class="flex flex-row items-center"
                                                @click="$v.$touch()"
                                            >
                                                <toggle-switch
                                                    v-model="tawkto.enabled"
                                                    :isDisabled="
                                                        !hasValidConfig
                                                    "
                                                    @toggle-enable="
                                                        toggleEnabled
                                                    "
                                                />
                                                <label
                                                    class="ml-2 text-sm text-gray-500"
                                                >
                                                    {{
                                                        tawkto.enabled
                                                            ? 'Enabled - Tawk.to live chat widget is active'
                                                            : 'Disabled - Tawk.to live chat widget is inactive'
                                                    }}
                                                </label>
                                            </div>
                                            <p
                                                v-if="!hasValidConfig"
                                                class="mt-1 text-sm text-red-600"
                                            >
                                                Please save the configuration
                                                first before enabling the
                                                integration.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <BaseSpacerWithRuler />

        <div class="my-6 ml-4 flex flex-shrink-0 justify-end">
            <span
                class="inline-flex rounded-md shadow-sm"
                @click="saveSettings"
            >
                <button
                    type="button"
                    class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none active:bg-indigo-700"
                    :disabled="isLoading"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch';

export default {
    name: 'TawktoSettings',
    components: { ToggleSwitch },
    metaInfo: {
        title: 'Tawk.to Integration'
    },
    props: {
        venueId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            hasExistingConfig: false,
            tawkto: {
                propertyId: '',
                widgetId: '',
                enabled: false
            }
        };
    },
    validations: {
        tawkto: {
            propertyId: {
                required
            },
            widgetId: {
                required
            }
        }
    },
    async created() {
        await this.fetchSettings();
    },
    computed: {
        hasValidConfig() {
            return this.hasExistingConfig;
        }
    },
    methods: {
        async fetchSettings() {
            try {
                this.isLoading = true;

                const { data } = await this.$axios.get(
                    `/venues/${this.venueId}/tawkto/config`
                );

                if (data.success && data.configured && data.config) {
                    this.hasExistingConfig = true;
                    this.tawkto = {
                        propertyId: data.config.propertyId || '',
                        widgetId: data.config.widgetId || '',
                        enabled: data.enabled || false
                    };
                } else {
                    this.hasExistingConfig = false;
                    this.tawkto = {
                        propertyId: '',
                        widgetId: '',
                        enabled: false
                    };
                }
            } catch (e) {
                if (e.response?.status === 404) {
                    this.hasExistingConfig = false;
                    this.tawkto = {
                        propertyId: '',
                        widgetId: '',
                        enabled: false
                    };
                } else {
                    console.error('Error fetching Tawk.to settings:', e);
                }
            } finally {
                this.isLoading = false;
            }
        },

        async toggleEnabled() {
            if (!this.hasValidConfig) {
                return;
            }

            try {
                this.tawkto.enabled = !this.tawkto.enabled;

                await this.$axios.post(
                    `/venues/${this.venueId}/tawkto/toggle`,
                    {
                        enabled: this.tawkto.enabled
                    }
                );

                this.$notify({
                    group: 'settings',
                    title: this.tawkto.enabled
                        ? 'Integration enabled'
                        : 'Integration disabled',
                    type: 'success'
                });
            } catch (error) {
                this.tawkto.enabled = !this.tawkto.enabled;

                this.$notify({
                    group: 'settings',
                    title: 'Failed to update integration status',
                    text:
                        'Please try again or contact support if the problem persists',
                    type: 'error'
                });

                console.error('Tawk.to toggle error:', error);
            }
        },

        async saveSettings() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.$notify({
                    group: 'settings',
                    title: 'Please fill in all required fields',
                    type: 'error'
                });
                return false;
            }

            try {
                this.isLoading = true;

                await this.$axios.post(
                    `/venues/${this.venueId}/tawkto/config`,
                    {
                        enabled: this.tawkto.enabled,
                        propertyId: this.tawkto.propertyId,
                        widgetId: this.tawkto.widgetId
                    }
                );

                this.hasExistingConfig = true;

                this.$notify({
                    group: 'settings',
                    title: 'Settings saved successfully',
                    type: 'success'
                });
            } catch (error) {
                console.error('Tawk.to config save error:', error);

                if (error.response?.data?.message) {
                    this.$notify({
                        group: 'settings',
                        title: 'Failed to save settings',
                        text: error.response.data.message,
                        type: 'error'
                    });
                } else {
                    this.$notify({
                        group: 'settings',
                        title: 'Failed to save settings',
                        text: 'Please check your configuration and try again',
                        type: 'error'
                    });
                }

                await this.fetchSettings();
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
